"use client";
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { ProfileControl } from "@owlcat/identity-react";
import { useTheme } from "@owlcat/theme";
import { DropdownMenuItem, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, } from "@owlcat/ui";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { openSubscribe, isSubscribeWidgetMounted, } from "./SubscribeWidget";
/** In-dropdown language submenu (renders inside ProfileControl's Radix menu). */
function LanguageMenu({ language }) {
    const { locale, locales, localeNames, parentDomain, label = "Language", onSelect } = language;
    function switchLocale(next) {
        if (next === locale)
            return;
        if (typeof window !== "undefined") {
            const parent = parentDomain?.trim().replace(/^\.+/, "");
            const host = window.location.hostname;
            const onParent = !!parent && (host === parent || host.endsWith(`.${parent}`));
            const domain = onParent ? `; Domain=.${parent}` : "";
            const secure = window.location.protocol === "https:" ? "; Secure" : "";
            document.cookie = `owlcat-locale=${next}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`;
        }
        if (onSelect) {
            onSelect(next);
            return;
        }
        const parts = window.location.pathname.split("/");
        parts[1] = next;
        window.location.assign((parts.join("/") || `/${next}/`) + window.location.search + window.location.hash);
    }
    return (_jsxs(DropdownMenuSub, { children: [_jsxs(DropdownMenuSubTrigger, { children: [label, ": ", localeNames[locale] ?? locale] }), _jsx(DropdownMenuSubContent, { children: locales.map((l) => (_jsx(DropdownMenuItem, { onSelect: () => switchLocale(l), children: localeNames[l] ?? l }, l))) })] }));
}
/** In-dropdown theme toggle item (keeps the menu open on click). */
function ThemeMenuItem({ label = "Toggle theme" }) {
    const { resolvedTheme, setTheme } = useTheme();
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    const isDark = resolvedTheme === "dark";
    return (_jsxs(DropdownMenuItem, { onSelect: (e) => {
            e.preventDefault();
            setTheme(isDark ? "light" : "dark");
        }, children: [label, mounted ? _jsx("span", { className: "ml-auto text-xs text-muted-foreground", children: isDark ? "◐" : "☀" }) : null] }));
}
export function AppHeaderControls({ profile, appId, language, themeLabel, subscribe, compact, className, }) {
    // Does the user hold an active subscription for THIS app? Drives the trigger
    // label (Subscribe vs Manage subscription). Best-effort; anon/401 → false. A visitor the
    // server already knows is anonymous (no fleet cookie) is not asked — the 401 would only
    // log a red line on every landing view.
    const [hasSubscription, setHasSubscription] = useState(false);
    useEffect(() => {
        if (!subscribe?.manageLabel || !appId || profile.knownAnonymous)
            return;
        let alive = true;
        const base = (profile.accountsUrl ?? "").replace(/\/+$/, "");
        fetch(`${base}/me/entitlements?app=${encodeURIComponent(appId)}`, {
            credentials: "include",
            headers: { Accept: "application/json" },
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
            if (alive)
                setHasSubscription((d?.activeTiers?.length ?? 0) > 0);
        })
            .catch(() => {
            /* leave false */
        });
        return () => {
            alive = false;
        };
    }, [subscribe?.manageLabel, appId, profile.accountsUrl, profile.knownAnonymous]);
    const subscribeTriggerLabel = subscribe && hasSubscription && subscribe.manageLabel ? subscribe.manageLabel : subscribe?.label;
    // AUTHED dropdown extras: Upgrade, language submenu, theme toggle.
    const menuExtras = (_jsxs(_Fragment, { children: [subscribe ? (_jsx(DropdownMenuItem, { "data-testid": "header-upgrade", onSelect: (e) => {
                    if (isSubscribeWidgetMounted()) {
                        e.preventDefault();
                        openSubscribe(appId);
                    }
                    else if (subscribe.fallbackUrl && typeof window !== "undefined") {
                        window.location.assign(subscribe.fallbackUrl);
                    }
                }, children: _jsx("span", { className: "font-medium text-primary", children: subscribeTriggerLabel }) })) : null, language ? _jsx(LanguageMenu, { language: language }) : null, _jsx(ThemeMenuItem, { label: themeLabel })] }));
    // ANON extras: compact language + theme beside the Sign-in button.
    const anonExtras = (_jsxs(_Fragment, { children: [language ? _jsx(LanguageSwitcher, { ...language }) : null, _jsx(ThemeToggle, { label: themeLabel })] }));
    return (_jsx("div", { className: `flex items-center gap-2 ${className ?? ""}`, children: _jsx(ProfileControl, { ...profile, appId: appId, compact: compact ?? profile.compact, locale: language?.locale, menuExtras: menuExtras, anonExtras: anonExtras }) }));
}
